import {
    Table,
    Header,
    Button,
    Box,
    Spinner,
    Alert,
    TableProps,
    CollectionPreferences,
} from "@amzn/awsui-components-react";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import { useCollection } from "@amzn/awsui-collection-hooks";
import React from 'react';
import useLocalStorage from "src/utils/useLocalStorage";
import { DEFAULT_PREFERENCES, PAGE_SIZE_OPTIONS } from "../common/Constants";
import { MDAResultsData, MDAResultsRow, ExcelHeaderData } from "../hooks/useMDAResultsData";
import '../styles.scss';

export interface MultidimensionalAllocationResultsTableProps {
    preSignedUrl?: string;
    /** The file the parent loaded from preSignedUrl; the past-view total reads the same download. */
    resultFile: MDAResultsData;
    /** Past view only: a new link for Download, because preSignedUrl expires five minutes after it is signed. */
    getDownloadUrl?: () => Promise<string | undefined>;
}

function formatAllocationValue(item: MDAResultsRow, index: number): React.ReactNode {
    const cellValue = item.row[index];
    
    if (cellValue === undefined || cellValue === null) {
        return '-';
    }

    // Format numeric values
    if (typeof cellValue === 'number') {
        return cellValue.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    }

    return String(cellValue);
}

function formatColumnHeader(header: ExcelHeaderData): React.ReactNode {
    return header.value;
}

/**
 * Generic component for displaying multidimensional allocation results from Excel data
 * Reads from S3 presign URL and displays the "Aggregated Totals" sheet data
 * Dynamically handles any number of rows and columns without validation
 */
export default function MultidimensionalAllocationResultsTable(props: MultidimensionalAllocationResultsTableProps) {
    const { preSignedUrl, resultFile, getDownloadUrl } = props;
    const sheetName = "Aggregated Totals"

    const { excelData, headers, loading, error } = resultFile;

    // Collection preferences with local storage
    const preferencesStorageKey = `allocation-results-table-preferences-${sheetName}`;
    const [preferences, setPreferences] = useLocalStorage.getStorageKeyFromLocalStorage(
        preferencesStorageKey,
        DEFAULT_PREFERENCES
    );

    // Create dynamic column definitions from headers
    const columnDefinitions: TableProps.ColumnDefinition<MDAResultsRow>[] = headers.map((header, index) => {
        const isNumericColumn = index > 0; // Assume all columns after first are numeric

        return {
            id: `col_${index}`,
            header: formatColumnHeader(header),
            cell: (item: MDAResultsRow) => {
                const formattedValue = formatAllocationValue(item, index);
                
                // Right-align numeric columns, left-align text columns
                return isNumericColumn ? (
                    <div style={{ textAlign: 'right' }}>{formattedValue}</div>
                ) : (
                    <div>{formattedValue}</div>
                );
            },
            sortingField: `col_${index}`
        };
    });

    // Text filtering function for Excel data
    const textFilterFunction = (item: MDAResultsRow, filterText: string): boolean => {
        if (!filterText) return true;
        const searchText = filterText.toLowerCase();
        
        // Search in all row values
        return item.row.some(cellValue => {
            if (cellValue === undefined || cellValue === null) return false;
            return String(cellValue).toLowerCase().includes(searchText);
        });
    };

    // Collection hook for filtering, sorting, and pagination
    const {
        items,
        filteredItemsCount,
        collectionProps,
        filterProps,
        paginationProps,
    } = useCollection(excelData, {
        filtering: {
            filteringFunction: textFilterFunction,
        },
        sorting: {},
        pagination: { pageSize: preferences.pageSize },
    });

    const handleDownload = async () => {
        const downloadUrl = getDownloadUrl ? await getDownloadUrl() : preSignedUrl;
        if (downloadUrl) {
            // Create a temporary link to download the file
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `allocation-results-${sheetName}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Handle refresh button click
    const handleRefresh = () => {
        // Force reload by updating a timestamp or similar trigger
        window.location.reload();
    };

    // Show loading state
    if (loading) {
        return (
            <Box textAlign="center" padding="l">
                <Spinner size="large" />
                <Box variant="p">Loading allocation results data...</Box>
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Alert type="error" header="Error loading allocation results">
                {error}
            </Alert>
        );
    }

    return (
        <div className="mda-results-table">
            <Table
                {...collectionProps}
                header={
                    <Header
                        variant="h2"
                        actions={
                            <Button onClick={handleDownload} disabled={!preSignedUrl}>
                                Download Excel
                            </Button>
                        }
                        counter={getFilterCounterText(filteredItemsCount)}
                    >
                        Pull Balance Results
                    </Header>
                }
                columnDefinitions={columnDefinitions}
                items={items}
                loading={loading}
                loadingText="Loading results..."
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No allocation results</b>
                        <Box variant="p" color="inherit">
                            {preSignedUrl ?
                                `No allocation details are currently available for the ${sheetName} sheet.` :
                                "No Excel file URL provided."
                            }
                        </Box>
                    </Box>
                }
                pagination={
                    <Pagination {...paginationProps} />
                }
                preferences={
                    <CollectionPreferences
                        title="Preferences"
                        confirmLabel="Confirm"
                        cancelLabel="Cancel"
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                        pageSizePreference={{
                            title: "Page Size",
                            options: PAGE_SIZE_OPTIONS,
                        }}
                        wrapLinesPreference={{
                            label: "Wrap Lines",
                            description: "Select to see all the text and wrap the lines"
                        }}
                        stripedRowsPreference={{
                            label: "Striped Rows",
                            description: "Select to add alternating shaded rows"
                        }}
                    />
                }
                wrapLines={preferences.wrapLines}
                stripedRows={preferences.stripedRows}
                sortingDisabled={true}
            />
        </div>
    );
}

function getFilterCounterText(count: number | undefined) {
    if (count && count > 0) {
        return ` (${count})`;
    }
    return undefined;
}
